import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Divider, Fab, Grid, IconButton, Tab, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { EntityTypes, PermissionDto, ResourceType } from '../../common/PricePrgClient';
import { StyledTabs } from '../../common/StyledComponents';
import { scrollableY } from '../../common/theme/scrollbar';
import { RootState } from '../../store';
import { getPermissions } from '../../utils';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import ConnectPricelistDialog from './ConnectPricelistDialog';

import CreatePricelistDialog from './CreatePricelistDialog';
import PricelistTree from './PricelistTree';
import { PricelistTreeItem } from './pricelistTypes';
import { getPricelistDialogState, getPricelists, getSelectedPricelistIds } from './redux/pricelistSelectors';
import {
    closeCreatePricelistDialog,
    deletePricelist,
    fetchPricelistPeriods,
    openCreatePricelistDialog,
    pricelistsSelected
} from './redux/pricelistSlices';
import CustomersPricelistTab from './tabs/Customers/CustomersPricelistTab';
import GeneralPricelistTab from './tabs/General/GeneralPricelistTab';
import { PricelistTimelineTab } from './tabs/PricelistTimeline/PricelistTimelineTab';

const mapStateToProps = (state: RootState) => ({
    selectedPricelistIds: getSelectedPricelistIds(state),
    currentUserPermissions: getCurrentUserPermissions(state),
    pricelists: getPricelists(state),
    createPricelistDialogState: getPricelistDialogState(state),
    appRestrictions: state.settings.applicationRestrictions,
    showConnectPricelistDialog: state.pricelist.ui.connectPricelistDialog.isVisible
});

type Props = ReturnType<typeof mapStateToProps>;

const Pricelist = ({
    selectedPricelistIds,
    currentUserPermissions,
    pricelists,
    createPricelistDialogState,
    appRestrictions,
    showConnectPricelistDialog
}: Props) => {
    const [tabIndex, setTabIndex] = useState(0);
    // const [hoveredPricelistId, setHoveredPricelistId] = useState(-1);
    const [createPricelistDialogModel, setCreatePricelistDialogModel] = useState({
        parentPricelist: null as {
            id: number;
            name: string;
            currency: string;
        } | null
    });
    const [filter, setFilter] = useState('');
    const dispatch = useDispatch();

    const canCreatePricelists = useMemo(() => {
        const key = EntityTypes.Pricelist.toString();
        if (appRestrictions && (appRestrictions[key]?.isCreateDisabled ?? false)) {
            return false;
        }
        if (currentUserPermissions.isAdmin) {
            return true;
        }

        const plPerms = currentUserPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.Pricelist
        );
        return plPerms?.recordPermissions.find(x => x.recordId === 0)?.canCreate;
    }, [currentUserPermissions, appRestrictions]);

    const pricelistPermissions: { [pricelistId: number]: PermissionDto } = useMemo(() => {
        const plPermObj: { [pricelistId: number]: PermissionDto } = {};
        const plPerms = getPermissions(
            ResourceType.Pricelist,
            [0, ...Object.keys(pricelists).map(Number)],
            currentUserPermissions
        );

        for (const plPerm of plPerms) {
            plPermObj[plPerm.recordId!] = plPerm;
        }

        return plPermObj;
    }, [currentUserPermissions, pricelists]);

    const handleSetSelectedPricelistIds = (pricelistIds: number[] | number) => {
        if (Array.isArray(pricelistIds)) {
            dispatch(pricelistsSelected(pricelistIds));
            dispatch(fetchPricelistPeriods.request(pricelistIds));
        } else {
            dispatch(pricelistsSelected([pricelistIds]));
        }
    };

    const handleTabChange = (newTabIndex: number) => {
        if (newTabIndex === 0 || newTabIndex === 2) {
            const selectedPricelistId = selectedPricelistIds.length > 0 ? [selectedPricelistIds[0]] : [];
            dispatch(pricelistsSelected(selectedPricelistId));
        } else if (newTabIndex === 1 && selectedPricelistIds.length > 0) {
            dispatch(fetchPricelistPeriods.request(selectedPricelistIds));
        }
        setTabIndex(newTabIndex);
    };

    const handleCreateNewPricelist = (
        parentPricelist: { id: number; name: string; currency: string } | null
    ) => {
        setCreatePricelistDialogModel({
            parentPricelist
        });
        dispatch(openCreatePricelistDialog());
    };

    const handleDeletePricelist = (pricelist: PricelistTreeItem) => {
        dispatch(
            showConfirmDialog({
                dialogTitle: `Delete ${pricelist.displayName}`,
                dialogMessage: `Do you want to delete ${pricelist.displayName}?`,
                primaryActionName: 'Delete',
                okAction: deletePricelist.request(pricelist.id)
            })
        );
    };

    const handleCloseCreateNewPricelistDialog = () => {
        setCreatePricelistDialogModel({
            parentPricelist: null
        });
        dispatch(closeCreatePricelistDialog());
    };

    const treeItemRenderer = (item: PricelistTreeItem) => {
        return (
            <Box
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    maxHeight: '25px',
                    justifyContent: 'space-between',
                    '& > div ': {
                        display: 'none'
                    },
                    '&:hover > div': {
                        display: 'block'
                    }
                }}
            >
                <Box component="span">{item.displayName}</Box>

                <Box component="div">
                    <Tooltip title={`Create new pricelist below ${item.name}`}>
                        <Box component="span">
                            <IconButton
                                onClick={() => handleCreateNewPricelist(item)}
                                color="primary"
                                disabled={!canCreatePricelists}
                                size="small"
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Tooltip title={`Delete pricelist ${item.name}`}>
                        <Box component="span">
                            <IconButton
                                onClick={() => handleDeletePricelist(item)}
                                color="secondary"
                                disabled={
                                    item.children.length > 0 ||
                                    !(
                                        currentUserPermissions.isAdmin ||
                                        pricelistPermissions[item.id]?.canDelete
                                    ) ||
                                    appRestrictions[EntityTypes.Pricelist]?.isDeleteDisabled
                                }
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
                {/* )} */}
            </Box>
        );
    };

    return (
        <>
            <Grid sx={{ flexFlow: 'row' }} container>
                <Grid item xs={3} sx={theme => ({ ...scrollableY(theme), maxWidth: '400px' })}>
                    <PricelistTree
                        multiSelect={tabIndex === 1}
                        pricelistsSelected={handleSetSelectedPricelistIds}
                        selectedPricelistIds={tabIndex === 1 ? selectedPricelistIds : selectedPricelistIds[0]}
                        treeItemRenderer={tabIndex === 1 ? undefined : treeItemRenderer}
                        header={
                            tabIndex === 0 || tabIndex === 2 ? (
                                <Box
                                    component="div"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        paddingRight: '9px',
                                        marginTop: '16px'
                                    }}
                                >
                                    <Tooltip title="Create new price list">
                                        <span>
                                            <Fab
                                                size="small"
                                                color="primary"
                                                onClick={() => handleCreateNewPricelist(null)}
                                                disabled={!canCreatePricelists}
                                            >
                                                <AddIcon />
                                            </Fab>
                                        </span>
                                    </Tooltip>
                                </Box>
                            ) : undefined
                        }
                        filter={filter}
                        setFilter={setFilter}
                    />
                </Grid>
                <Divider orientation="vertical" flexItem />
                <Grid item xs={9} sx={{ paddingLeft: '10px', maxWidth: '1200px' }}>
                    <StyledTabs
                        value={tabIndex}
                        onChange={(_, val) => handleTabChange(val)}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="General" />
                        <Tab label="Pricelist periods" />
                        <Tab label="Customers" />
                    </StyledTabs>

                    <Box p={2}>
                        {tabIndex === 0 && <GeneralPricelistTab />}
                        {tabIndex === 1 && <PricelistTimelineTab />}
                        {tabIndex === 2 && <CustomersPricelistTab />}
                    </Box>
                </Grid>
            </Grid>

            {createPricelistDialogState.isOpen && (
                <CreatePricelistDialog
                    closeDialog={handleCloseCreateNewPricelistDialog}
                    parentPricelist={createPricelistDialogModel.parentPricelist}
                />
            )}
            {showConnectPricelistDialog && (
                <ConnectPricelistDialog customerId={undefined} pricelistId={selectedPricelistIds[0]} />
            )}
        </>
    );
};

export default connect(mapStateToProps)(Pricelist);
